import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Send, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const reasons = [
  { value: 'general', label: 'Consulta general' },
  { value: 'booking', label: 'Problema con mi reserva' },
  { value: 'payment', label: 'Consulta sobre pagos' },
  { value: 'business', label: 'Programa para empresas' },
  { value: 'professional', label: 'Quiero ser profesional' },
  { value: 'other', label: 'Otro motivo' }
];

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    reason: '',
    email: '',
    booking_id: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reason || !formData.message) {
      toast.error('Por favor completa el motivo y mensaje');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contacto`, {
        reason: formData.reason,
        message: formData.message,
        email: formData.email || null,
        booking_id: formData.booking_id || null
      });

      setSubmitted(true);
      toast.success('Mensaje enviado correctamente');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 py-20">
        <div className="container-max mx-auto px-4 md:px-8">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="p-12">
              <div className="w-20 h-20 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-6">
                <Send className="w-10 h-10 text-sage-600" />
              </div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                ¡Mensaje recibido!
              </h1>
              <p className="text-slate-600 mb-8">
                Gracias por contactarnos. Responderemos lo antes posible.
                {formData.email && ' Te notificaremos al email proporcionado.'}
              </p>
              <Link to="/">
                <Button className="btn-primary">
                  Volver al inicio
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-teal-600 py-16 md:py-24">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
              <MessageSquare className="w-3 h-3 mr-1" />
              Contacto
            </Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
              ¿Cómo podemos ayudarte?
            </h1>
            <p className="text-lg text-teal-100 leading-relaxed">
              Estamos aquí para resolver tus dudas. El email es opcional - 
              puedes identificarte solo con tu ID de reserva si lo prefieres.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl">Envíanos un mensaje</CardTitle>
                <CardDescription>
                  Responderemos en un plazo máximo de 24-48 horas hábiles.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="reason">Motivo de contacto *</Label>
                    <Select 
                      value={formData.reason} 
                      onValueChange={(value) => setFormData({...formData, reason: value})}
                    >
                      <SelectTrigger data-testid="contact-reason">
                        <SelectValue placeholder="Selecciona un motivo" />
                      </SelectTrigger>
                      <SelectContent>
                        {reasons.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email <span className="text-slate-500">(opcional)</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      data-testid="contact-email"
                    />
                    <p className="text-xs text-slate-500">
                      Solo para enviarte nuestra respuesta. No es obligatorio.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="booking_id">
                      ID de Reserva <span className="text-slate-500">(si aplica)</span>
                    </Label>
                    <Input
                      id="booking_id"
                      placeholder="ej: abc123-def456..."
                      value={formData.booking_id}
                      onChange={(e) => setFormData({...formData, booking_id: e.target.value})}
                      data-testid="contact-booking-id"
                    />
                    <p className="text-xs text-slate-500">
                      Si tu consulta es sobre una reserva específica.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mensaje *</Label>
                    <Textarea
                      id="message"
                      placeholder="Cuéntanos en qué podemos ayudarte..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="privacy-badge">
                    <Lock className="w-4 h-4" />
                    Tu mensaje está protegido y es confidencial
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={submitting}
                    data-testid="contact-submit"
                  >
                    {submitting ? 'Enviando...' : 'Enviar mensaje'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Emergency Notice */}
            <div className="mt-8 p-6 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-900 mb-2">
                    ¿Necesitas ayuda urgente?
                  </p>
                  <p className="text-sm text-amber-800">
                    AnonimaMente no es un servicio de emergencias. Si necesitas ayuda inmediata:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-amber-800">
                    <li>
                      <a href="tel:600 360 7777" className="font-semibold hover:underline">
                        Salud Responde: 600 360 7777
                      </a>
                    </li>
                    <li>
                      <a href="tel:*4141" className="font-semibold hover:underline">
                        Fono Ayuda: *4141
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactoPage;
